import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Printer, FileText, Archive, AlertTriangle } from 'lucide-react';

import ToolbarBtn from '../components/ToolbarBtn';
import SectionHead from '../components/SectionHead';
import FilingCard from '../components/FilingCard';
import EmptyBox from '../components/EmptyBox';
import ResourceLinkList from '../components/ResourceLinkList';

import { TOKENS, toolbarStyle } from '../styles';
import type { FilingsFilters } from '../types';

interface Props {
  onResourcesOpen: () => void;
  onPrint: () => void;
}

export default function FilingsTab({ onResourcesOpen, onPrint }: Props) {
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();
  const nextYear = currentYear + 1;
  const isLeapYear = nextYear % 4 === 0 && (nextYear % 100 !== 0 || nextYear % 400 === 0);
  const t4DueDate = (isLeapYear ? '29' : '28') + '/02/' + nextYear;
  const t4PeriodStart = '01/01/' + currentYear;
  const t4PeriodEnd = '31/12/' + currentYear;

  // Filter popovers are not currently rendered; state kept for future.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [filters, setFilters] = useState<FilingsFilters>({ periods: [], statuses: [] });

  return (
    <>
      <div style={{
        background: TOKENS.amberTint,
        border: '1px solid ' + TOKENS.amberLine,
        borderRadius: 12,
        padding: '14px 16px',
        display: 'flex',
        gap: 12,
        marginTop: 18,
      }}>
        <div style={{ color: TOKENS.amber }}>
          <AlertTriangle size={20} strokeWidth={2.5} />
        </div>
        <div>
          <div style={{ fontWeight: 700, color: TOKENS.amberText, fontSize: 14 }}>
            Select a dental benefits code for each employee
          </div>
          <div style={{ color: TOKENS.dark, fontSize: 13, marginTop: 2, fontWeight: 600 }}>
            The CRA needs to know who has access to any dental benefits you offer. This info is required to file T4 slips. Add it on each employee's deductions and contributions page.{' '}
            <a
              onClick={() => navigate('/payroll/employees?filter=missing-dental')}
              style={{ color: TOKENS.tealInk, fontWeight: 700, cursor: 'pointer' }}
            >
              Set dental codes
            </a>
          </div>
        </div>
      </div>

      <div style={toolbarStyle}>
        <div style={{ flex: 1 }} />
        <ToolbarBtn icon={<Printer size={14} strokeWidth={2.5} />} label="Print" onClick={onPrint} />
        <ToolbarBtn icon={<FileText size={14} strokeWidth={2.5} />} label="Resources" onClick={onResourcesOpen} />
        <ToolbarBtn icon={<Archive size={14} strokeWidth={2.5} />} label="Archive" onClick={() => navigate('/payroll/taxes/archived')} />
      </div>

      <SectionHead label="ACTION NEEDED" count={0} />
      <EmptyBox text="Filings that need your attention will show here." />

      <SectionHead label="COMING UP" count={3} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <FilingCard
          title="T4 summary"
          sub={t4PeriodStart + ' to ' + t4PeriodEnd}
          dueDate={t4DueDate}
          method="Manually file"
          onPreview={() => window.open('/payroll/taxes/t4-preview/summary', '_blank')}
        />
        <FilingCard
          title="T4 employer slips"
          sub="Employer copy of T4 slips"
          dueDate={t4DueDate}
          method="Manually file with XML"
          onPreview={() => window.open('/payroll/taxes/t4-preview/employer', '_blank')}
        />
        <FilingCard
          title="T4 employee slips"
          sub="T4 slip for employee"
          dueDate={t4DueDate}
          method="Manually file"
          onPreview={() => window.open('/payroll/taxes/t4-preview/employee', '_blank')}
        />
      </div>

      <SectionHead label="DONE" count={0} />
      <EmptyBox text="Filings you've completed this year will show here." />

      <SectionHead label="FILING RESOURCES" />
      <ResourceLinkList
        items={[
          { title: 'Record of employment', desc: 'The ROEs you have created for your employees.' },
          { title: 'Archived forms and filings', desc: 'Completed tax filings and forms ready to view and print.', onClick: () => navigate('/payroll/taxes/archived') },
          { title: 'All filings resources', desc: 'Tax filings, federal tax forms, and other compliance resources.', onClick: onResourcesOpen },
        ]}
      />
    </>
  );
}